import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import SiteLayout from "@/components/site/SiteLayout";
import AdminLayout from "@/components/admin/AdminLayout";
import Login from "@/pages/Login";
import DashHome from "@/pages/DashHome";
import Demand from "@/pages/Demand";
import CashCost from "@/pages/CashCost";
import Prices from "@/pages/Prices";
import Capacity from "@/pages/Capacity";
import Capex from "@/pages/Capex";
import Home from "@/pages/site/Home";
import Reports from "@/pages/site/Reports";
import ReportDetail from "@/pages/site/ReportDetail";
import ContentPage from "@/pages/site/ContentPage";
import Contact from "@/pages/site/Contact";
import Account from "@/pages/site/Account";
import Subscribe from "@/pages/site/Subscribe";
import Basket from "@/pages/site/Basket";
import DigestUnsubscribe from "@/pages/site/DigestUnsubscribe";
import { ForgotPassword, ResetPassword } from "@/pages/site/PasswordPages";
import AdminSettings from "@/pages/admin/AdminSettings";
import { BasketSync } from "@/components/BasketSync";
import TechTransfer from "@/pages/site/TechTransfer";
import PlantAdvisory from "@/pages/site/PlantAdvisory";
import AdminData from "@/pages/admin/AdminData";
import AdminReports from "@/pages/admin/AdminReports";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminUsage from "@/pages/admin/AdminUsage";
import AdminPages from "@/pages/admin/AdminPages";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";

function Protected({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center font-mono text-xs text-slate-500">LOADING…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [pathname, search]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <BasketSync />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/dashboards/login" element={<Navigate to="/login?next=/dashboards" replace />} />
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/:id" element={<ReportDetail />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/basket" element={<Basket />} />
            <Route path="/digest/unsubscribe" element={<DigestUnsubscribe />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/tech-transfer" element={<TechTransfer />} />
            <Route path="/plant-advisory" element={<PlantAdvisory />} />
            <Route path="/:slug" element={<ContentPage />} />
          </Route>
          <Route path="/dashboards" element={<Layout />}>
            <Route index element={<DashHome />} />
            <Route path="demand" element={<Demand />} />
            <Route path="cash-cost" element={<CashCost />} />
            <Route path="prices" element={<Prices />} />
            <Route path="capacity" element={<Capacity />} />
            <Route path="capex" element={<Capex />} />
          </Route>
          <Route path="/admin" element={<Protected admin><AdminLayout /></Protected>}>
            <Route index element={<AdminUsage />} />
            <Route path="data" element={<AdminData />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="pricing" element={<AdminPricing />} />
            <Route path="subscribers" element={<AdminSubscribers />} />
            <Route path="pages" element={<AdminPages />} />
            <Route path="enquiries" element={<AdminEnquiries />} />
            <Route path="testimonials" element={<AdminTestimonials />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
